import React, { useRef, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  TitleComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';
import dayjs from 'dayjs';

echarts.use([
  TooltipComponent,
  TitleComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const getDefaultOptions = (theme, getThemeColor, data) => {
  // Extract keys from the data object
  const keysData = Object.keys(data); // Top-level keys like CAPSTAN_CURRENT, BOW_CURRENT, etc.
  const categories = [];
  const seriesData = [];

  // Extract unique machine names (categories)
  keysData.forEach(key => {
    const machineData = data[key];
    if (machineData && typeof machineData === 'object') {
      const machines = Object.keys(machineData);
      machines.forEach(machine => {
        if (!categories.includes(machine)) {
          categories.push(machine);
        }
      });
    }
  });

  // Create series for each key
  keysData.forEach(key => {
    const machineData = data[key];
    if (machineData && typeof machineData === 'object') {
      const seriesValues = categories.map(machine => {
        // Align values with correct category; fill missing machines with 0
        const machineArray = machineData[machine];
        return machineArray?.[machineArray.length - 1]?.[1] || 0; // Last value or 0
      });

      seriesData.push({
        name: key, // e.g., CAPSTAN_CURRENT, BOW_CURRENT
        type: 'bar',
        data: seriesValues, // Aligned data
        barWidth: '12px',
        itemStyle: {
          borderRadius: [2, 2, 0, 0], // Example styling
          color: getThemeColor('secondary') // Example color
        }
      });
    }
  });

  return {
    color: seriesData.map(series => series.itemStyle.color),
    tooltip: {
      trigger: 'item', // Show tooltip for individual bars (not 'axis')
      formatter: params => {
        // Customize tooltip content
        return `${params.seriesName}: ${params.value}`;
      }
    },
    xAxis: {
      type: 'category',
      data: categories, // Machines as x-axis categories
      axisLabel: {
        color: getThemeColor('gray-800'),
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('gray-300') }
      },
      axisLabel: {
        color: getThemeColor('gray-900'),
        formatter: '{value} amps'
      }
    },
    series: seriesData,
    grid: {
      right: 40,
      left: 20,
      bottom: '30px', // Adjust to accommodate labels
      top: '10%',
      containLabel: true
    },
    animation: false,
    barCategoryGap: '30%' // Space between categories
  };
};

const CurrentConsumedBarChart = ({ data, title }) => {
  const chartRef = useRef(null);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(theme, getThemeColor, data)}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default memo(CurrentConsumedBarChart);
